import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/use-toast';
import { productService } from '@/lib/packages';

type ProductRow = Database['public']['Tables']['products']['Row'];

export interface ProductFilters {
  searchTerm?: string;
  category?: string;
  companyId: string;
  locationId?: string;
  minStock?: number;
  excludeId?: string;
  includeAllLocations?: boolean; // New flag to show products from all locations
}

export function useProducts() {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchProducts = async (filters: ProductFilters) => {
    setLoading(true);
    try {
      // Use paginated getAllProducts to fetch all products (handles >1000 rows)
      // Then apply filters client-side for complex filtering
      const allProducts = await productService.getAllProducts(filters.companyId, {
        locationId: filters.locationId && !filters.includeAllLocations ? filters.locationId : undefined,
        showAllLocations: filters.includeAllLocations,
        includeInactive: false
      });

      // Apply filters client-side
      let filtered = allProducts;

      // Apply search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(searchLower) ||
          p.item_code?.toLowerCase().includes(searchLower)
        );
      }

      // Apply category filter
      if (filters.category) {
        filtered = filtered.filter(p => p.category === filters.category);
      }

      // Apply minimum stock filter
      if (filters.minStock !== undefined) {
        filtered = filtered.filter(p => (p.quantity_on_hand || 0) >= filters.minStock!);
      }

      // Exclude specific product
      if (filters.excludeId) {
        filtered = filtered.filter(p => p.id !== filters.excludeId);
      }

      setProducts(filtered);
    } catch (error) {
      console.error('Error in fetchProducts:', error);
      toast({
        title: "Hata",
        description: "Ürünler yüklenemedi",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getProductById = async (productId: string) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();

      if (error) {
        console.error('Error fetching product:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getProductById:', error);
      return null;
    }
  };

  return {
    products,
    loading,
    fetchProducts,
    getProductById,
  };
}
